import { useEffect, useState } from 'react'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import { FleetCluster, FLEET_HULL_UNITS, type FleetMarkerData } from './FleetMarker'
import { classifyRoute, type RouteFocus } from './useMapFocus'
import { groupOverlapping, headingAngle, screenConstant, type Point } from './geometry'

const SHEATH = '#04181f'

/** Une flotte en vol (mission ou raid PvP), résolue en données pures. */
export interface RouteFleet {
  key: string
  /**
   * Île visée, ou `null` pour une destination qui n'en est pas une : la chasse
   * légendaire vise un point de haute mer. Sert au focus (surligner la route de
   * l'île sélectionnée) ; une route sans île n'y participe simplement pas.
   */
  destinationIslandId: number | null
  origin: Point
  destination: Point
  color: string
  count: number
  label: string
  destName: string
  startedAt: string
  resolvesAt: string
}

/** Position courante d'une flotte le long de son aller-retour (lerp 0..0.5..0). */
function fleetPosition(f: RouteFleet, now: number): Point {
  const start = new Date(f.startedAt).getTime()
  const end = new Date(f.resolvesAt).getTime()
  const total = Math.max(1, end - start)
  const progress = Math.max(0, Math.min(1, (now - start) / total))
  const t = progress < 0.5 ? progress * 2 : (1 - progress) * 2
  return { x: f.origin.x + (f.destination.x - f.origin.x) * t, y: f.origin.y + (f.destination.y - f.origin.y) * t }
}

/** Point juste en amont de la destination, pour poser la flèche hors de l'île. */
function arrowAnchor(from: Point, to: Point, backoff: number): Point {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const len = Math.hypot(dx, dy) || 1
  return { x: to.x - (dx / len) * backoff, y: to.y - (dy / len) * backoff }
}

function RouteTrace({
  fleet,
  focus,
  reduced,
  k,
}: {
  fleet: RouteFleet
  focus: RouteFocus
  reduced: boolean
  k: number
}) {
  const lit = focus === 'focus'
  const dim = focus === 'dim'
  const groupOpacity = lit ? 0.95 : dim ? 0.18 : 0.5
  const animate = lit && !reduced

  const w = screenConstant(0.6, k)
  const gw = screenConstant(1.4, k)
  const dash = `${screenConstant(1.5, k)} ${screenConstant(1.2, k)}`
  const dashShift = -screenConstant(2.7, k)

  const ang = headingAngle(fleet.origin, fleet.destination)
  const anchor = arrowAnchor(fleet.origin, fleet.destination, screenConstant(3.5, k))
  const arrowScale = screenConstant(1, k)

  return (
    <g data-role="route" opacity={groupOpacity}>
      <line
        data-role="route-sheath"
        x1={fleet.origin.x}
        y1={fleet.origin.y}
        x2={fleet.destination.x}
        y2={fleet.destination.y}
        stroke={SHEATH}
        strokeWidth={gw}
        strokeLinecap="round"
        opacity={0.5}
      />
      <line
        data-role="route-line"
        x1={fleet.origin.x}
        y1={fleet.origin.y}
        x2={fleet.destination.x}
        y2={fleet.destination.y}
        stroke={fleet.color}
        strokeWidth={w}
        strokeDasharray={dash}
        strokeLinecap="round"
      >
        {animate && (
          <animate attributeName="stroke-dashoffset" values={`0;${dashShift}`} dur="0.9s" repeatCount="indefinite" />
        )}
      </line>
      {(lit || !dim) && (
        <g data-role="route-arrow" transform={`translate(${anchor.x} ${anchor.y}) scale(${arrowScale}) rotate(${ang})`}>
          <path
            d="M -2.6 -1.5 L 0 0 L -2.6 1.5"
            fill="none"
            stroke={fleet.color}
            strokeWidth={0.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={lit ? 1 : 0.7}
          />
        </g>
      )}
    </g>
  )
}

/**
 * Toutes les routes en vol dans un overlay SVG (viewBox monde, étiré sur le
 * carré). Trace par route : gaine sombre + trait coloré pointillé + pointe de
 * flèche orientée. Baseline calme (rien de sélectionné) ; la route dont la
 * destination est sélectionnée (ou celle survolée) passe en focus animé, les
 * autres s'estompent. Les marqueurs de flotte sont regroupés s'ils se chevauchent.
 */
export function RouteLayer({
  fleets,
  gridSize,
  k,
  selectedIslandId,
  hoveredKey,
  onHover,
  onSelectFleet,
}: {
  fleets: RouteFleet[]
  gridSize: number
  k: number
  selectedIslandId: number | null
  hoveredKey: string | null
  onHover?: (key: string | null) => void
  onSelectFleet?: (key: string) => void
}) {
  const reduced = usePrefersReducedMotion()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (fleets.length === 0) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [fleets.length])

  if (fleets.length === 0) return null

  // On n'active le focus par sélection que si une route mène réellement à l'île
  // sélectionnée (sinon sélectionner une île sans mission estomperait tout).
  const selIsDest = selectedIslandId !== null && fleets.some((f) => f.destinationIslandId === selectedIslandId)
  const effSelected = selIsDest ? selectedIslandId : null

  // Position courante de chaque flotte + clustering anti-chevauchement.
  const markers: FleetMarkerData[] = fleets.map((f) => {
    const pos = fleetPosition(f, now)
    return {
      key: f.key,
      x: pos.x,
      y: pos.y,
      origin: f.origin,
      destination: f.destination,
      color: f.color,
      count: f.count,
      label: f.label,
      destName: f.destName,
    }
  })
  const clusters = groupOverlapping(markers, FLEET_HULL_UNITS / Math.max(k, 0.0001))

  return (
    <svg
      viewBox={`0 0 ${gridSize} ${gridSize}`}
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full"
      // Les marqueurs/routes captent le survol (desktop) ; le pan reste géré par RZPP.
      style={{ pointerEvents: 'none' }}
    >
      {fleets.map((f) => (
        <g
          key={`trace-${f.key}`}
          style={{
            pointerEvents: onHover || onSelectFleet ? 'stroke' : 'none',
            cursor: onSelectFleet ? 'pointer' : undefined,
          }}
          onMouseEnter={() => onHover?.(f.key)}
          onMouseLeave={() => onHover?.(null)}
          onClick={onSelectFleet ? () => onSelectFleet(f.key) : undefined}
        >
          {/* Bande de clic/survol transparente, plus large que le trait visible
              (qui est fin) → la route se sélectionne au clic comme le marqueur. */}
          <line
            data-role="route-hit"
            x1={f.origin.x}
            y1={f.origin.y}
            x2={f.destination.x}
            y2={f.destination.y}
            stroke="transparent"
            strokeWidth={screenConstant(3, k)}
            strokeLinecap="round"
          />
          <RouteTrace fleet={f} focus={classifyRoute(f.key, f.destinationIslandId, effSelected, hoveredKey)} reduced={reduced} k={k} />
        </g>
      ))}
      {clusters.map((c) => (
        <FleetCluster
          key={`cl-${c.items.map((m) => m.key).sort().join('|')}`}
          cluster={c}
          k={k}
          onSelect={onSelectFleet}
        />
      ))}
    </svg>
  )
}
